import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';
import { Layers, Building2, Users, ShieldCheck, Save, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DOC_TYPES = ['coi', 'w9', 'license', 'workers_comp', 'auto_insurance', 'umbrella', 'bond', 'safety_document', 'contract'];
const ENDORSEMENTS = ['additional_insured', 'waiver_of_subrogation', 'primary_noncontributory', 'per_project_aggregate', 'blanket_additional_insured'];
const COVERAGE_FIELDS = ['general_liability', 'each_occurrence', 'aggregate', 'workers_compensation', 'auto_liability', 'umbrella', 'professional_liability'];

function RequirementEditor({ data, onChange, showWcExempt = true }) {
  const docs = data?.required_documents || [];
  const coverage = data?.coverage_minimums || {};
  const endos = data?.endorsements || [];
  const licenses = data?.license_requirements || [];

  const toggleDoc = (dt) => {
    const updated = docs.includes(dt) ? docs.filter(d => d !== dt) : [...docs, dt];
    onChange({ ...data, required_documents: updated });
  };
  const toggleEndo = (e) => {
    const updated = endos.includes(e) ? endos.filter(x => x !== e) : [...endos, e];
    onChange({ ...data, endorsements: updated });
  };
  const setCoverage = (field, value) => {
    onChange({ ...data, coverage_minimums: { ...coverage, [field]: value ? Number(value) : null } });
  };

  return (
    <div className="space-y-5">
      <div>
        <Label className="text-sm font-medium">Required Documents</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          {DOC_TYPES.map(dt => (
            <div key={dt} className="flex items-center gap-2">
              <Checkbox checked={docs.includes(dt)} onCheckedChange={() => toggleDoc(dt)} />
              <Label className="text-xs uppercase cursor-pointer">{dt.replace(/_/g, ' ')}</Label>
            </div>
          ))}
        </div>
      </div>
      <Separator />
      <div>
        <Label className="text-sm font-medium">Coverage Minimums ($)</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {COVERAGE_FIELDS.map(f => (
            <div key={f} className="flex items-center gap-2">
              <Label className="text-xs w-32 capitalize">{f.replace(/_/g, ' ')}</Label>
              <Input type="number" placeholder="0" className="flex-1" value={coverage[f] || ''} onChange={e => setCoverage(f, e.target.value)} />
            </div>
          ))}
        </div>
      </div>
      <Separator />
      <div>
        <Label className="text-sm font-medium">Required Endorsements</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {ENDORSEMENTS.map(e => (
            <div key={e} className="flex items-center gap-2">
              <Checkbox checked={endos.includes(e)} onCheckedChange={() => toggleEndo(e)} />
              <Label className="text-xs capitalize cursor-pointer">{e.replace(/_/g, ' ')}</Label>
            </div>
          ))}
        </div>
      </div>
      <Separator />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Expiration Window (days)</Label>
          <Input type="number" value={data?.expiration_window_days || 30} onChange={e => onChange({ ...data, expiration_window_days: Number(e.target.value) })} className="mt-1" />
        </div>
        {showWcExempt && (
          <div className="flex items-center justify-between">
            <div><Label className="text-xs">Workers Comp Exemption</Label><p className="text-xs text-muted-foreground">Skip WC requirement</p></div>
            <Switch checked={data?.workers_comp_exemption || false} onCheckedChange={v => onChange({ ...data, workers_comp_exemption: v })} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ComplianceSettingsPage() {
  const [vendorTypes, setVendorTypes] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('');
  const [selectedProp, setSelectedProp] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [vtRes, propRes, vendorRes] = await Promise.all([
        api.get('/requirements/vendor-types'),
        api.get('/properties'),
        api.get('/vendors'),
      ]);
      setVendorTypes(vtRes.data.items || []);
      setProperties(propRes.data.items || []);
      setVendors(vendorRes.data.items || []);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Load type data
  useEffect(() => {
    if (selectedType) {
      api.get(`/requirements/vendor-types/${selectedType}`).then(r => setEditData(r.data)).catch(() => {});
    }
  }, [selectedType]);

  // Load property override
  useEffect(() => {
    if (selectedProp) {
      api.get(`/requirements/properties/${selectedProp}`).then(r => setEditData(r.data.has_overrides === false ? { required_documents: [], coverage_minimums: {}, endorsements: [], license_requirements: [], expiration_window_days: null, workers_comp_exemption: null } : r.data)).catch(() => {});
    }
  }, [selectedProp]);

  // Load vendor override
  useEffect(() => {
    if (selectedVendor) {
      api.get(`/requirements/vendors/${selectedVendor}`).then(r => setEditData(r.data.has_overrides === false ? { required_documents: [], coverage_minimums: {}, endorsements: [], license_requirements: [], expiration_window_days: null, workers_comp_exemption: null } : r.data)).catch(() => {});
    }
  }, [selectedVendor]);

  const handleSaveType = async () => {
    setSaving(true);
    try {
      await api.put(`/requirements/vendor-types/${selectedType}`, editData);
      toast.success(`${selectedType} requirements saved`);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleSaveProp = async () => {
    setSaving(true);
    try {
      await api.put(`/requirements/properties/${selectedProp}`, editData);
      toast.success('Property overrides saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleSaveVendor = async () => {
    setSaving(true);
    try {
      await api.put(`/requirements/vendors/${selectedVendor}`, editData);
      toast.success('Vendor overrides saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 rounded-xl" /></div>;

  return (
    <div data-testid="compliance-settings-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Compliance Requirements</h1>
        <p className="text-sm text-muted-foreground">Three-layer hierarchy: Vendor Type → Property Override → Vendor Override</p>
      </div>

      <Tabs defaultValue="vendor-types">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="vendor-types"><Layers className="mr-2 h-4 w-4" /> Vendor Types</TabsTrigger>
          <TabsTrigger value="properties"><Building2 className="mr-2 h-4 w-4" /> Property Overrides</TabsTrigger>
          <TabsTrigger value="vendors"><Users className="mr-2 h-4 w-4" /> Vendor Overrides</TabsTrigger>
        </TabsList>

        {/* Vendor Type Requirements */}
        <TabsContent value="vendor-types" className="space-y-4">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendor Type Requirements (Default Layer)</CardTitle>
              <CardDescription>Set default compliance requirements for each vendor category. All vendors of this type inherit these rules.</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedType} onValueChange={v => { setSelectedType(v); setEditData(null); }}>
                <SelectTrigger className="max-w-xs mb-4" data-testid="vt-type-select"><SelectValue placeholder="Select vendor type" /></SelectTrigger>
                <SelectContent>
                  {vendorTypes.map(vt => <SelectItem key={vt.vendor_type} value={vt.vendor_type}>{vt.vendor_type.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedType && editData && (
                <>
                  <RequirementEditor data={editData} onChange={setEditData} />
                  <Button className="mt-4" onClick={handleSaveType} disabled={saving} data-testid="vt-save-button">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save {selectedType} Requirements
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Property Overrides */}
        <TabsContent value="properties" className="space-y-4">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Property-Level Overrides (Optional Layer)</CardTitle>
              <CardDescription>Override vendor type defaults for a specific property. These augment or replace the base requirements.</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedProp} onValueChange={v => { setSelectedProp(v); setEditData(null); }}>
                <SelectTrigger className="max-w-xs mb-4" data-testid="prop-override-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedProp && editData && (
                <>
                  <div className="bg-muted/50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-muted-foreground">Only set values you want to override. Empty fields inherit from the vendor type defaults. Coverage values here replace the defaults (can be higher or lower).</p>
                  </div>
                  <RequirementEditor data={editData} onChange={setEditData} />
                  <Button className="mt-4" onClick={handleSaveProp} disabled={saving} data-testid="prop-save-button">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Property Overrides
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vendor Overrides */}
        <TabsContent value="vendors" className="space-y-4">
          <Card className="rounded-xl shadow-[var(--shadow-soft)]">
            <CardHeader>
              <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendor-Specific Overrides (Exception Layer)</CardTitle>
              <CardDescription>Override requirements for individual vendors. These always take precedence over type and property rules.</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedVendor} onValueChange={v => { setSelectedVendor(v); setEditData(null); }}>
                <SelectTrigger className="max-w-xs mb-4" data-testid="vendor-override-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name} ({v.vendor_type})</SelectItem>)}
                </SelectContent>
              </Select>
              {selectedVendor && editData && (
                <>
                  <div className="bg-muted/50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-muted-foreground">Vendor-level overrides always win. Common use: workers comp exemption for sole proprietors, custom coverage for high-risk vendors.</p>
                  </div>
                  <RequirementEditor data={editData} onChange={setEditData} showWcExempt={false} />
                  <Button className="mt-4" onClick={handleSaveVendor} disabled={saving} data-testid="vendor-save-button">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Vendor Overrides
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
